import { Info } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { IndicatorsDocument } from 'lib/api/collections/types';
import CustomButton from 'lib/designSystem/Button';
import CentralPieChart, {
  CentralPieChartItem,
} from 'lib/designSystem/Charts/CentralPieChart';
import ColoredCircle from 'lib/designSystem/ColoredCircle';
import EnhancedTable from 'lib/designSystem/Table';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

type DocumentsDialogProps = {
  openDialog: boolean;
  handleDialogClose: () => void;
  documentsForInsights: IndicatorsDocument[];
  showDocumentType: string;
  selectedSegment: CentralPieChartItem;
};

const DocumentsDialog = ({
  openDialog,
  handleDialogClose,
  documentsForInsights,
  showDocumentType,
  selectedSegment,
}: DocumentsDialogProps) => {
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState<CentralPieChartItem | null>(
    null,
  );

  const unsortedDocumentsForInsights = selectedItem
    ? documentsForInsights.filter(
        (document) => document.collectorName === selectedItem.name,
      )
    : documentsForInsights;
  const filteredDocumentsForInsights = unsortedDocumentsForInsights.sort(
    (a, b) => b.amount - a.amount,
  );

  const documentsForInsightsChartDataSource = documentsForInsights.reduce(
    (
      acc: Record<string, { amount: number; count: number }>,
      document: IndicatorsDocument,
    ) => {
      const collectorName = document.collectorName;
      if (acc[collectorName]) {
        acc[collectorName] = {
          amount: acc[collectorName].amount + document.amount,
          count: acc[collectorName].count + 1,
        };
      } else {
        acc[collectorName] = { amount: document.amount, count: 1 };
      }
      return acc;
    },
    {},
  );

  const documentsForInsightsChartData = Object.entries(
    documentsForInsightsChartDataSource,
  ).map(([name, value]) => ({
    name,
    value: showDocumentType === 'amount' ? value.amount : value.count,
  }));

  const amountByBusinessTableDataSource = filteredDocumentsForInsights.reduce(
    (
      acc: Record<
        string,
        {
          id: string;
          amount: number;
          count: number;
        }
      >,
      document: IndicatorsDocument,
    ) => {
      const businessName = document.businessName;
      if (acc[businessName]) {
        acc[businessName] = {
          id: document.businessIdentifier,
          amount: acc[businessName].amount + document.amount,
          count: acc[businessName].count + 1,
        };
      } else {
        acc[businessName] = {
          id: document.businessIdentifier,
          amount: document.amount,
          count: 1,
        };
      }
      return acc;
    },
    {},
  );

  const totalAmountByBusiness = Object.values(
    amountByBusinessTableDataSource,
  ).reduce(
    (acc: number, value: { id: string; amount: number; count: number }) =>
      acc + value.amount,
    0,
  );
  const totalCountByBusiness = Object.values(
    amountByBusinessTableDataSource,
  ).reduce(
    (acc: number, value: { id: string; amount: number; count: number }) =>
      acc + value.count,
    0,
  );

  const amountByBusinessTableData = Object.entries(
    amountByBusinessTableDataSource,
  )
    .map(([name, value]) => ({
      id: value.id,
      name,
      amount: value.amount,
      count: value.count,
      percentageAmount: value.amount / totalAmountByBusiness,
      percentageCount: value.count / totalCountByBusiness,
    }))
    .sort((a, b) =>
      showDocumentType === 'amount' ? b.amount - a.amount : b.count - a.count,
    );

  const amountByStakeholderTableDataSource =
    filteredDocumentsForInsights.reduce(
      (
        acc: Record<string, { id: string; amount: number; count: number }>,
        document: IndicatorsDocument,
      ) => {
        const stakeholderName = document.stakeholderName;
        if (acc[stakeholderName]) {
          acc[stakeholderName] = {
            id: document.stakeholderIdentifier ?? '',
            amount: acc[stakeholderName].amount + document.amount,
            count: acc[stakeholderName].count + 1,
          };
        } else {
          acc[stakeholderName] = {
            id: document.stakeholderIdentifier ?? '',
            amount: document.amount,
            count: 1,
          };
        }
        return acc;
      },
      {},
    );

  const totalAmountByStakeholder = Object.values(
    amountByStakeholderTableDataSource,
  ).reduce(
    (acc: number, value: { id: string; amount: number; count: number }) =>
      acc + value.amount,
    0,
  );
  const totalCountByStakeholder = Object.values(
    amountByStakeholderTableDataSource,
  ).reduce(
    (acc: number, value: { amount: number; count: number }) =>
      acc + value.count,
    0,
  );

  const amountByStakeholderTableData = Object.entries(
    amountByStakeholderTableDataSource,
  )
    .map(([name, value]) => ({
      id: value.id,
      name,
      amount: value.amount,
      count: value.count,
      percentageAmount: value.amount / totalAmountByStakeholder,
      percentageCount: value.count / totalCountByStakeholder,
    }))
    .sort((a, b) =>
      showDocumentType === 'amount' ? b.amount - a.amount : b.count - a.count,
    );

  const handleShowBusinessDetails = (id: string) => {
    const row = filteredDocumentsForInsights.find(
      (x: IndicatorsDocument) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: string) => {
    const row = filteredDocumentsForInsights.find(
      (x: IndicatorsDocument) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier ?? '',
        name: row.stakeholderName ?? '',
      }),
    );
  };

  const handleShowBusinessDetails2 = (id: string) => {
    const row = amountByBusinessTableData.find(
      (x: { id: string }) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.id,
        name: row.name,
      }),
    );
  };

  const handleShowStakeholderDetails2 = (id: string) => {
    const row = amountByStakeholderTableData.find(
      (x: { id: string }) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.id,
        name: row.name,
      }),
    );
  };

  const handlePieChartClick = (entry: CentralPieChartItem) => {
    setSelectedItem(entry);
  };

  return (
    <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="xl">
      <DialogTitle>
        <ColoredCircle color={selectedSegment?.color ?? ''} size={15} />
        {` ${selectedSegment?.name}${selectedItem ? ` - ${selectedItem.name}` : ''}`}
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4} sm={4}>
              <Typography align="center">
                Selecciona para ver detalle por ejecutivo
              </Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Typography align="center">Por cliente</Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Typography align="center">Por deudor</Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <CentralPieChart
                data={documentsForInsightsChartData}
                showLegend={false}
                height={300}
                onClick={handlePieChartClick}
              />
            </Grid>
            <Grid item xs={4} sm={4}>
              <EnhancedTable
                dataHeaders={[
                  {
                    key: 'name',
                    label: 'Cliente',
                    handleAction: handleShowBusinessDetails2,
                    icon: <Info fontSize="small" />,
                  },
                  ...(showDocumentType === 'amount'
                    ? [
                        { key: 'amount', label: 'Monto', type: 'money' },
                        {
                          key: 'percentageAmount',
                          label: '%',
                          type: 'percentage',
                        },
                      ]
                    : [
                        { key: 'count', label: 'Cantidad', type: 'numeric-id' },
                        {
                          key: 'percentageCount',
                          label: '%',
                          type: 'percentage',
                        },
                      ]),
                ]}
                data={amountByBusinessTableData}
                exportToFile="xlsx"
              />
            </Grid>
            <Grid item xs={4} sm={4}>
              <EnhancedTable
                dataHeaders={[
                  {
                    key: 'name',
                    label: 'Deudor',
                    handleAction: handleShowStakeholderDetails2,
                    icon: <Info fontSize="small" />,
                  },
                  ...(showDocumentType === 'amount'
                    ? [
                        { key: 'amount', label: 'Monto', type: 'money' },
                        {
                          key: 'percentageAmount',
                          label: '%',
                          type: 'percentage',
                        },
                      ]
                    : [
                        { key: 'count', label: 'Cantidad', type: 'numeric-id' },
                        {
                          key: 'percentageCount',
                          label: '%',
                          type: 'percentage',
                        },
                      ]),
                ]}
                data={amountByStakeholderTableData}
                exportToFile="xlsx"
              />
            </Grid>
          </Grid>
          <Typography>Detalle de documentos</Typography>
          <EnhancedTable
            dataHeaders={[
              { key: 'documentTypeEsp', label: 'Tipo' },
              { key: 'folio', label: 'Folio' },
              { key: 'collectorName', label: 'Ejecutivo' },
              {
                key: 'businessName',
                label: 'Cliente',
                handleAction: handleShowBusinessDetails,
                icon: <Info fontSize="small" />,
              },
              {
                key: 'stakeholderName',
                label: 'Deudor',
                handleAction: handleShowStakeholderDetails,
                icon: <Info fontSize="small" />,
              },
              {
                key: 'paymentDate',
                label: 'Vencimiento',
                type: 'date',
              },
              { key: 'amount', label: 'Monto', type: 'money' },
            ]}
            data={filteredDocumentsForInsights}
            exportToFile="xlsx"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleDialogClose}>Cerrar</CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentsDialog;
